// src/pages/sangamam/sangamamContent.jsx
// New content sections added to match the updated Pon Anandham Sangamam
// page copy. Nothing here touches the contact form / enquiry form / lead
// submission logic — those stay exactly as they are in
// components/shared/enquiryForm/enquiryForm.jsx and
// components/footer/contactForm.jsx. These are purely presentational
// sections that get dropped into sangamam.jsx.
import React from "react";
import Accordion from "react-bootstrap/Accordion";
import { DownloadBrochure } from "../../components/download_brochure/download_brochure";
import "./sangamamContent.css";

// Shared helper: scrolls to the existing enquiry form rendered in the
// footer (id="sangamam-contact-form") — same element BookVisitButton
// already scrolls to, so behaviour stays consistent site-wide.
const scrollToEnquiryForm = () => {
  const formEl = document.getElementById("sangamam-contact-form");
  if (formEl) {
    formEl.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};

/* ----------------------------------------------------------------
   1. Hero highlight strip — badges + "Book a Site Visit" CTA,
      sits directly under the existing banner image.
------------------------------------------------------------------*/
export const HeroHighlights = () => {
  const badges = [
    "DTCP & RERA Approved",
    "Double Side Road Access",
    "Premium Gated Community",
    "High Appreciation Potential",
  ];
  return (
    <div className="sangamam-hero-strip">
      <div className="sangamam-hero-badges">
        {badges.map((b, i) => (
          <span className="hero-badge" key={i}>
            <span className="hero-badge-tick">✓</span> {b}
          </span>
        ))}
      </div>
      <button className="hero-cta-btn" onClick={scrollToEnquiryForm}>
        Book a Site Visit
      </button>
    </div>
  );
};

/* ----------------------------------------------------------------
   2. Why Invest in Thiruporur
------------------------------------------------------------------*/
export const WhyInvestThiruporur = () => (
  <div className="thiruporur-invest-section">
    <h2 className="sangamam-features-heading green-text heading-bold">
      Why Invest in Thiruporur?
    </h2>
    <div className="thiruporur-invest-content">
      <h4>Chennai's Fastest Growing Investment Corridor</h4>
      <p>
        Thiruporur has rapidly emerged as one of South Chennai's fastest-growing
        residential and investment destinations. Strategically located between
        OMR, ECR, and GST Road, the region is driven by the growth of Siruseri
        SIPCOT, OneHub Chennai, expanding industries, world-class educational
        institutions, and upcoming infrastructure developments.
      </p>
      <p>
        With increasing residential demand and continuous commercial growth,
        villa plots in Thiruporur offer excellent long-term appreciation, making
        it one of the smartest investment locations near Chennai.
      </p>
    </div>
  </div>
);

/* ----------------------------------------------------------------
   3. Premium Amenities
------------------------------------------------------------------*/
const amenitiesList = [
  "Grand Entrance Arch",
  "Wide 60 & 40-ft Blacktop Roads",
  "LED Street Lighting",
  "Landscaped Parks",
  "Children's Play Area",
  "Avenue Tree Plantation",
  "CCTV Surveillance",
  "Stormwater Drainage",
  "Dedicated Water Supply",
  "Public Utility Spaces",
  "Clear Title Documentation",
];

export const PremiumAmenities = () => (
  <div className="premium-amenities-section">
    <h2 className="sangamam-features-heading green-text heading-bold">
      Premium Amenities
    </h2>
    <p className="amenities-subtext">
      Every detail at Pon Anandham Sangamam has been thoughtfully planned to
      offer a secure, convenient and family-friendly lifestyle.
    </p>
    <div className="amenities-grid">
      {amenitiesList.map((item, i) => (
        <div className="amenity-chip" key={i}>
          <span className="amenity-tick">✓</span>
          {item}
        </div>
      ))}
    </div>
  </div>
);

/* ----------------------------------------------------------------
   4. Location Advantages (qualitative cards — distinct from the
      distance accordion / map further down the page)
------------------------------------------------------------------*/
const locationAdvList = [
  {
    emoji: "🏭",
    title: "Siruseri SIPCOT",
    text: "A leading IT hub driving employment and residential demand.",
  },
  {
    emoji: "🏢",
    title: "OneHub Chennai",
    text: "A 1,450-acre Japanese Industrial Township with global companies like Yamaha and Hitachi.",
  },
  {
    emoji: "🛣️",
    title: "OMR",
    text: "Easy access to Chennai's leading technology and business parks.",
  },
  {
    emoji: "🌊",
    title: "ECR",
    text: "Quick connectivity to Chennai's coastal lifestyle destinations.",
  },
  {
    emoji: "🎓",
    title: "Reputed Schools & Colleges",
    text: "Close to leading educational institutions for quality learning.",
  },
  {
    emoji: "🏥",
    title: "Hospitals",
    text: "Easy access to multi-speciality healthcare facilities.",
  },
  {
    emoji: "🎢",
    title: "Wonderla Chennai",
    text: "Enjoy entertainment and family recreation just a short drive away.",
  },
];

export const LocationAdvantages = () => (
  <div className="location-adv-section">
    <h2 className="sangamam-features-heading green-text heading-bold">
      Location Advantages
    </h2>
    <p className="location-adv-subtext">
      Everything you need, just minutes away.
    </p>
    <div className="location-adv-grid">
      {locationAdvList.map((item, i) => (
        <div className="location-adv-card" key={i}>
          <div className="location-adv-emoji">{item.emoji}</div>
          <h4>{item.title}</h4>
          <p>{item.text}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ----------------------------------------------------------------
   5. Plot specs table (price / sizes / approvals)
------------------------------------------------------------------*/
const specRows = [
  ["Price", "Starting from ₹2,599 per sq.ft."],
  ["Plot Sizes", "1,088 sq.ft. to 2,515 sq.ft."],
  ["Project Type", "Premium Residential Villa Plots"],
  ["Location", "Thiruporur–Chengalpattu 4-Lane National Highway"],
  ["Approvals", "DTCP & RERA Approved"],
];

export const PlotSpecs = () => (
  <div className="plot-specs-section">
    <h2 className="sangamam-features-heading green-text heading-bold">
      Premium Residential Villa Plots in Thiruporur
    </h2>
    <p className="plot-specs-subtext">
      Discover premium residential villa plots thoughtfully planned for modern
      living and long-term investment on the Thiruporur–Chengalpattu 4-Lane
      National Highway.
    </p>
    <div className="plot-specs-table-wrap">
      <table className="plot-specs-table">
        <tbody>
          {specRows.map(([label, val], i) => (
            <tr key={i}>
              <td className="spec-label">{label}</td>
              <td className="spec-value">{val}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ----------------------------------------------------------------
   6. FAQ accordion
------------------------------------------------------------------*/
/* const faqData = [
  {
    q: "Is Pon Anandham Sangamam DTCP approved?",
    a: "Yes, the project offers legally approved villa plots with transparent documentation.",
  },
  {
    q: "Where is Pon Anandham Sangamam located?",
    a: "The project is located on the Thiruporur-Chengalpattu Highway with excellent connectivity to OMR, ECR and GST Road.",
  },
  {
    q: "Why should I invest in Thiruporur?",
    a: "Thiruporur is one of South Chennai's fastest-growing corridors, supported by IT hubs, industrial growth and infrastructure development.",
  },
  {
    q: "How close is Siruseri SIPCOT?",
    a: "The project enjoys convenient access to Siruseri SIPCOT, making it ideal for IT professionals and investors.",
  },
  {
    q: "What is OneHub Chennai?",
    a: "OneHub Chennai is a 1,450-acre Japanese Industrial Township that contributes to employment growth and increasing residential demand in the region.",
  },
  {
    q: "Can I start construction immediately?",
    a: "Yes, the ready-to-build villa plots allow you to begin construction as per applicable guidelines.",
  },
  {
    q: "Why are villa plots in Thiruporur a smart investment?",
    a: "Villa plots in Thiruporur offer excellent appreciation potential due to their proximity to OMR, Siruseri SIPCOT, OneHub Chennai, and major infrastructure developments.",
  },
  {
    q: "Is Pon Anandham Sangamam a gated community?",
    a: "Yes. Pon Anandham Sangamam is a premium gated community offering modern infrastructure, wide roads, landscaped parks, and essential amenities.",
  },
  {
    q: "Are bank loans available?",
    a: "Yes. Bank loan assistance is available for eligible buyers.",
  },
  {
    q: "What amenities are available?",
    a: "The project includes wide roads, LED street lighting, landscaped parks, children's play area, CCTV surveillance, water supply and more.",
  },
  {
    q: "Is the project close to OMR?",
    a: "Yes. Pon Anandham Sangamam enjoys excellent connectivity to OMR, making it convenient for professionals working in Chennai's IT Corridor.",
  },
  {
    q: "Is this suitable for investment?",
    a: "Yes. The project's strategic location and strong growth potential make it an attractive long-term investment.",
  },
  {
    q: "How can I book a site visit?",
    a: "Contact our sales team to schedule a free site visit at your convenience.",
  },
]; */
/* ----------------------------------------------------------------- */
const faqData = [
  {
    q: "What makes Pon Anandham Sangamam a good choice for plots in Thiruporur?",
    a: "Pon Anandham Sangamam offers residential villa plots in Thiruporur with convenient access to OMR and the Thiruporur–Chengalpattu corridor. With ready-to-construct plots and a gated community setting, Sangamam is suitable for both homebuyers and long-term property investors.",
  },
  {
    q: "Where is Pon Anandham Sangamam located?",
    a: "Pon Anandham Sangamam is located near Thiruporur, off OMR, along the Thiruporur–Chengalpattu 4-lane highway. Its location offers convenient connectivity to OMR and key destinations across South Chennai.",
  },
  {
    q: "Are there plots for sale in Thiruporur at Pon Anandham Sangamam?",
    a: "Yes. Pon Anandham Sangamam offers residential plots for sale in Thiruporur, including villa plots in multiple sizes. The project is designed for buyers looking to build their own home as well as investors looking for residential land in a developing corridor.",
  },
  {
    q: "What is the price of plots in Thiruporur at Pon Anandham Sangamam?",
    a: "The price of plots varies based on the plot size, location and availability. Contact our team for the latest pricing.",
  },
  {
    q: "Are there villa plots for sale in Thiruporur?",
    a: "Yes. Pon Anandham Sangamam offers villa plots in Thiruporur, giving buyers the flexibility to build their own home.",
  },
  {
    q: "Is Pon Anandham Sangamam a gated community?",
    a: "Yes. Pon Anandham Sangamam is a gated residential plot community with planned infrastructure and amenities.",
  },
  {
    q: "Is Pon Anandham Sangamam DTCP and RERA approved?",
    a: "Yes. Pon Anandham Sangamam is a DTCP and RERA-approved residential plot development.",
  },
  {
    q: "How close is Pon Anandham Sangamam to OMR?",
    a: "Pon Anandham Sangamam is located off OMR near Thiruporur, providing easy access to the OMR corridor and its growing residential, commercial and employment hubs.",
  },
  {
    q: "Why should I invest in plots in Thiruporur?",
    a: "Thiruporur is part of a rapidly developing South Chennai growth corridor with improving connectivity and expanding infrastructure. Investing in residential plots in Thiruporur can offer buyers the flexibility to build a home while also providing potential for long-term property appreciation.",
  },
  {
    q: "Is Pon Anandham Sangamam suitable for investment?",
    a: "Yes. Pon Anandham Sangamam is suitable for buyers looking for residential plots for investment or future home construction.",
  },
  {
    q: "Can I start construction after purchasing a plot?",
    a: "The plots are planned for residential construction. Buyers can confirm the applicable approvals and construction requirements with the project team.",
  },
  {
    q: "Are bank loans available for these plots?",
    a: "Loan availability depends on the buyer's eligibility and the lending institution. Contact the project team for details on available financing options.",
  },
  {
    q: "What plot sizes are available at Pon Anandham Sangamam?",
    a: "Pon Anandham Sangamam offers residential plots in multiple sizes to suit different home requirements and budgets. Contact the team for current availability.",
  },
  {
    q: "What amenities and infrastructure are available at Sangamam?",
    a: "Sangamam offers a gated community setting with planned infrastructure, internal roads and residential amenities.",
  },
  {
    q: "How can I book a site visit for Pon Anandham Sangamam?",
    a: "Contact the Pon Anandham Sangamam team to schedule a site visit and explore the available plots in Thiruporur.",
  },
];
/* ----------------------------------------------------------------- */
export const SangamamFAQ = () => (
  <div className="sangamam-faq-section">
    <h2 className="sangamam-features-heading green-text heading-bold">
      Frequently Asked Questions
    </h2>
    <Accordion className="sangamam-faq-accordion" flush>
      {faqData.map((item, i) => (
        <Accordion.Item eventKey={String(i)} key={i}>
          <Accordion.Header>{item.q}</Accordion.Header>
          <Accordion.Body>{item.a}</Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
  </div>
);

/* ----------------------------------------------------------------
   7. Closing CTA banner — reuses the existing DownloadBrochure
      component (modal + ContactForm) exactly as-is, and scrolls to
      the same enquiry form as BookVisitButton for "Schedule Site Visit".
------------------------------------------------------------------*/
export const ClosingCTA = () => (
  <div className="sangamam-closing-cta">
    <h2>Own Your Villa Plot in South Chennai's Growth Corridor</h2>
    <p>
      Whether you're planning your dream home or looking for a smart investment,
      Pon Anandham Sangamam offers the perfect combination of location,
      connectivity, premium infrastructure and future appreciation.
    </p>
    <div className="closing-cta-buttons">
      <button className="closing-cta-btn" onClick={scrollToEnquiryForm}>
        Schedule Site Visit
      </button>
      <div className="closing-cta-brochure">
        <DownloadBrochure />
      </div>
    </div>
  </div>
);
